import React, { useEffect, useRef, useState } from "react";
import "./ImageSlider.scss";

export interface Props {
  Images: string[];
  sliderData?: React.ReactNode[];
  sliderDots?: boolean;
  sliderNavigation?: boolean;
  sliderDelay?: number;
  arrowLeftText?: React.ReactNode;
  arrowRightText?: React.ReactNode;
  SliderClass?: string;
  SliderId?: string;
  navigationCustomClass?: string;
  dotsCustomClass?: string;
  sliderContentClass?: string;
  sliderImageClass?: string;
}

const ImageSlider = ({
  Images,
  sliderData = [],
  sliderDots = false,
  sliderNavigation = false,
  sliderDelay = 2500,
  arrowLeftText = "Prev",
  arrowRightText = "Next",
  SliderClass,
  SliderId,
  navigationCustomClass,
  dotsCustomClass,
  sliderContentClass,
  sliderImageClass,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  const totalSlides = Images.length;

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleArrowRight = () => {
    if (totalSlides === 0) return;

    setActiveIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };

  const handleArrowLeft = () => {
    if (totalSlides === 0) return;

    setActiveIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  useEffect(() => {
    if (totalSlides === 0) return;

    resetTimeout();

    timeoutRef.current = window.setTimeout(() => {
      setActiveIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    }, sliderDelay);

    return resetTimeout;
  }, [totalSlides, sliderDelay, activeIndex]);

  if (totalSlides === 0) return null;

  return (
    <div
      className={`mfg-slideWrapper${SliderClass ? " " + SliderClass : ""}`}
      id={SliderId}
    >
      <div
        className="slideContainer"
        style={{ transform: `translate3d(${-activeIndex * 100}%, 0, 0)` }}
      >
        {Images.map((image, index) => (
          <div
            key={index}
            className={`slide${activeIndex === index ? " active" : ""}`}
          >
            <div
              className={`sliderImage${
                sliderImageClass ? " " + sliderImageClass : ""
              }`}
            >
              <img src={image} alt={`slide-${index}`} />
            </div>

            {sliderData[index] && (
              <div
                className={`sliderContent${
                  sliderContentClass ? " " + sliderContentClass : ""
                }`}
              >
                {sliderData[index]}
              </div>
            )}
          </div>
        ))}
      </div>

      {sliderNavigation && (
        <div
          className={`slideNavigation${
            navigationCustomClass ? " " + navigationCustomClass : ""
          }`}
        >
          <button onClick={handleArrowLeft} className="leftArrow">
            {arrowLeftText}
          </button>
          <button onClick={handleArrowRight} className="rightArrow">
            {arrowRightText}
          </button>
        </div>
      )}

      {sliderDots && (
        <div
          className={`slideDotsContainer${
            dotsCustomClass ? " " + dotsCustomClass : ""
          }`}
        >
          {Images.map((_, index) => (
            <button
              key={index}
              className={`slideDots ${activeIndex === index ? "active" : ""}`}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageSlider;
